import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Clock, RefreshCw, ArrowRight } from 'lucide-react';
import { useFarmFromUrl } from '@/shared/hooks/useUrlSync';
import { PageLoading } from '@/shared/components/ui/LoadingSpinner';
import { FarmRequiredGuard } from '@/shared/components/FarmRequiredGuard';
import { Button } from '@/shared/components/ui/Button';
import { queryKeys } from '@/shared/api/queryClient';
import { buildRoute } from '@/shared/lib/constants';
import { morningSummaryApi } from './api';
import { QuickActionsBar } from './components/QuickActionsBar';
import { QuickStatsGrid } from './components/QuickStatsGrid';
import { PondsAttentionCard } from './components/PondsAttentionCard';
import { RecentAlertsCard } from './components/RecentAlertsCard';
import { YesterdayActivityCard } from './components/YesterdayActivityCard';
import { AIInsightsCard } from './components/AIInsightsCard';
import { EmptyState } from './components/EmptyState';
import { MorningSummarySkeleton } from './components/MorningSummarySkeleton';

export const MorningSummaryPage: React.FC = () => {
  const { farmId, farm, isLoading: isFarmLoading } = useFarmFromUrl();

  // Fetch 24h summary data
  const {
    data: summary,
    isLoading,
    isRefetching,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: queryKeys.morningSummary.farm(farmId || ''),
    queryFn: () => morningSummaryApi.getMorningSummary(farmId!),
    enabled: !!farmId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });

  // Calculate time since last refresh
  const getTimeSinceRefresh = (): string => {
    const now = new Date();
    const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : now;
    const diffMs = now.getTime() - lastUpdated.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    return 'Over an hour ago';
  };

  // Manual refresh handler
  const handleRefresh = async () => {
    await refetch();
  };

  // Show farm loading
  if (isFarmLoading) {
    return <PageLoading message="Loading farm..." />;
  }

  // Show farm selection guard if no farmId
  if (!farmId) {
    return (
      <FarmRequiredGuard farmId={farmId} isLoading={isFarmLoading}>
        {null}
      </FarmRequiredGuard>
    );
  }

  // Show skeleton while loading
  if (isLoading) {
    return <MorningSummarySkeleton />;
  }

  // Check if there's nothing needing attention (show celebration state)
  // Use optional chaining to handle null/undefined arrays from API
  const showEmptyState =
    summary &&
    (summary.pondsNeedingAttention?.length ?? 0) === 0 &&
    (summary.recentAlerts?.length ?? 0) === 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
          <span>{farm?.name}</span>
          <span>›</span>
          <span className="text-gray-900 font-medium">Recent Overview</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200/50">
              <Clock className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <h1 className="font-roboto text-2xl font-bold text-gray-900">Recent Overview</h1>
              <p className="font-roboto mt-1 text-sm text-gray-600">
                Recent activity and actionable insights for your farm
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Last updated indicator */}
            <span className="text-sm text-gray-500">Updated {getTimeSinceRefresh()}</span>
            {/* Refresh button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefetching}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {/* View All Ponds link */}
            <Link to={buildRoute.ponds(farmId)}>
              <Button variant="outline" size="sm">
                View All Ponds
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <QuickActionsBar farmId={farmId} />

      {/* Quick Stats Grid - only render if quickStats exists */}
      {summary?.quickStats && <QuickStatsGrid stats={summary.quickStats} />}

      {/* Show empty state or content */}
      {showEmptyState ? (
        <EmptyState stats={summary?.quickStats} />
      ) : (
        <>
          {/* Two-column layout for main content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Ponds Needing Attention - pass empty array if null */}
            <PondsAttentionCard
              farmId={farmId}
              ponds={summary?.pondsNeedingAttention ?? []}
            />

            {/* Recent Alerts - pass empty array if null */}
            <RecentAlertsCard
              farmId={farmId}
              alerts={summary?.recentAlerts ?? []}
            />
          </div>

          {/* Two-column layout for activity and AI */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Yesterday's Activity - only render if activity data exists */}
            {summary?.yesterdayActivity && (
              <YesterdayActivityCard
                farmId={farmId}
                activity={summary.yesterdayActivity}
                totalPonds={summary.quickStats?.totalPondCount ?? 0}
              />
            )}

            {/* AI Insights (only if there are insights) */}
            {summary?.aiInsights && summary.aiInsights.length > 0 && (
              <AIInsightsCard
                farmId={farmId}
                insights={summary.aiInsights}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
